import styled, { keyframes } from 'styled-components/macro';
import { components } from 'react-select';
import { mediumScreens, smallScreens, lowHeightScreens } from '../fontElements';
import * as colors from 'components/common/colorConstants';

const mediaQueryMediumScreens = `@media screen and (max-width: ${mediumScreens})`;

const mediaQueryLowSmallScreens = `@media screen and (max-height: ${lowHeightScreens}), screen and (max-width: ${smallScreens})`;

const reactSelectSpinnerAnimationSpin = keyframes`
to { transform: rotate(360deg); }
`;

const SpinnerReactSelect = styled.span`
  animation: ${reactSelectSpinnerAnimationSpin} 0.4s linear infinite;
  animation-timing-function: linear;
  height: 16px;
  width: 16px;
  box-sizing: border-box;
  border-radius: 50%;
  border: 2px solid ${colors.lightGray18};
  border-right-color: ${colors.lightGray18};
  display: inline-block;
  margin-bottom: 2px;
`;

export const IndicatorSeparator = () => null;

export const DropdownIndicator = (props) => {
  const { selectProps } = props;
  const isOpen = selectProps.menuIsOpen;

  return (
    <components.DropdownIndicator {...props}>
      <img
        src={isOpen ? '/images/new-designs/chevron-up-blue.svg' : '/images/new-designs/chevron-down-blue.svg'}
        alt={isOpen ? 'Collapse dropdown' : 'Expand dropdown'}
        style={{ width: '8px', height: '4px' }}
      />
    </components.DropdownIndicator>
  );
};

export const LoadingIndicator = (props) => {
  return <SpinnerReactSelect />;
};

/**
 * This is the new way on how react-select handles styling the component.
 * This makes it on the one hand easier to style the component but on the other hand it uses an
 * object structure now and not css directtly as styled-components would do.
 *
 * This approach is called 'styles props' and is the one recommended by the library.
 *
 * Reference: https://react-select.com/styles#the-styles-prop
 */
export const commonReactSelectStyles = {
  clearIndicator: (baseStyles) => ({
    ...baseStyles,
    paddingRight: '0',
    svg: {
      height: '18px !important',
      width: '18px !important',
      ':hover': {
        fill: colors.venetianRed,
      },
    },
  }),
  dropdownIndicator: (baseStyles, state) => ({
    ...baseStyles,
    paddingLeft: '0',
  }),
  control: (baseStyles, state) => ({
    ...baseStyles,
    height: '25px !important',
    minHeight: 'initial',
    borderColor: state.menuIsOpen ? `${colors.blue1} !important` : `${colors.lightGray21} !important`,
    backgroundColor: `${colors.baseBackground} !important`,
    boxShadow: 'none',
    borderRadius: '4px',
    '&:hover': {
      borderColor: state.menuIsOpen ? `${colors.blue1} !important` : `${colors.lightGray21} !important`,
      boxShadow: 'none',
    },
    ...(state.isMulti
      ? {
          height: 'auto !important',
        }
      : {}),
  }),
  indicatorsContainer: (baseStyles) => ({
    ...baseStyles,
    cursor: 'pointer',
    height: '23px',
  }),
  placeholder: (baseStyles) => ({
    ...baseStyles,
    fontSize: '11px',
    color: colors.inputGrey,
    [mediaQueryMediumScreens]: {
      fontSize: '10px',
    },
    [mediaQueryLowSmallScreens]: {
      fontSize: '10px',
    },
  }),
  singleValue: (baseStyles) => ({
    ...baseStyles,
    cursor: 'pointer',
    fontSize: '11px',
    color: colors.baseText,
    [mediaQueryMediumScreens]: {
      fontSize: '10px',
    },
    [mediaQueryLowSmallScreens]: {
      fontSize: '10px',
    },
  }),
  input: (baseStyles) => ({
    ...baseStyles,
    fontSize: '11px',
    color: colors.baseText,
    height: '21px',
    margin: '0',
    [mediaQueryMediumScreens]: {
      fontSize: '10px',
    },
    [mediaQueryLowSmallScreens]: {
      fontSize: '10px',
    },
  }),
  menu: (baseStyles) => ({
    ...baseStyles,
    fontSize: '11px',
    marginTop: '4px',
    borderRadius: '4px',
    backgroundColor: colors.baseBackground,
    border: `1px solid ${colors.lightGray21}`,
    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.12)',
    [mediaQueryMediumScreens]: {
      fontSize: '10px',
    },
    [mediaQueryLowSmallScreens]: {
      fontSize: '10px',
    },
  }),
  menuList: (baseStyles) => ({
    ...baseStyles,
    paddingTop: '0',
    paddingBottom: '0',
  }),
  option: (baseStyles, state) => ({
    ...baseStyles,
    color: colors.baseText,
    backgroundColor: state.isFocused ? colors.lightGray7 : colors.baseBackground,
    cursor: 'pointer',
  }),
  noOptionsMessage: (baseStyles, state) => ({
    ...baseStyles,
    textAlign: 'initial',
  }),
  loadingMessage: (baseStyles, state) => ({
    ...baseStyles,
    textAlign: 'initial',
  }),
  valueContainer: (baseStyles) => ({
    ...baseStyles,
    padding: '2px 4px',
  }),
  multiValue: (baseStyles) => ({
    ...baseStyles,
    height: '19px',
    color: colors.baseText,
    fontSize: '11px',
    backgroundColor: colors.lightGray7,
    border: 'none',
    borderRadius: '4px',
    margin: '1px 2px',
    fontFamily: "'Roboto', sans-serif",
    fontWeight: 400,
    [mediaQueryMediumScreens]: {
      fontSize: '10px',
    },
    [mediaQueryLowSmallScreens]: {
      fontSize: '10px',
    },
  }),
  multiValueLabel: (baseStyles) => ({
    ...baseStyles,
    color: colors.baseText,
    fontSize: '11px',
    fontFamily: "'Roboto', sans-serif",
    fontWeight: 400,
    padding: '2px 4px',
    paddingLeft: '6px',
    lineHeight: '15px',
  }),
  multiValueRemove: (baseStyles) => ({
    ...baseStyles,
    color: colors.baseText,
    cursor: 'pointer',
    padding: '2px 4px',
    paddingRight: '6px',
    backgroundColor: 'transparent',
    lineHeight: '15px',
    fontFamily: "'Roboto', sans-serif",
    fontWeight: 400,
    '&:hover': {
      backgroundColor: 'transparent',
      color: colors.blue1,
    },
  }),
};
